/**
 * Blanks in a Template's title and text: the parts that change each time it's
 * used. Written inline as `{% type: Name %}` (e.g. `{% date: Closing date %}`),
 * or the older untyped `{% name %}`, which means Text. The type lives in the
 * text itself, so no backend changes are needed.
 */
import { formatEventDisplayDate } from "../../utils/eventDateDisplay";
import {
  escapeNoticeBodyText,
  isPlainNoticeBody,
} from "../../utils/noticeBodyHtml";
import { formatLedgerCurrency } from "../../utils/schoolFeesHelpers";
import {
  TEMPLATE_BLANK_TYPE_PATTERN,
  type TemplateBlankType,
} from "./templateBlankTypes";

export { TEMPLATE_BLANK_TYPES, type TemplateBlankType } from "./templateBlankTypes";

export type TemplateBlank = {
  /** Identity: the name lower-cased with spacing collapsed. */
  key: string;
  /** The name as first written. */
  name: string;
  /** What a Team member sees next to the input. */
  label: string;
  type: TemplateBlankType;
};

/** A raw input value per Blank, keyed by `TemplateBlank.key`. */
export type TemplateBlankValues = Record<string, string>;

const BLANK_RE = new RegExp(
  `\\{%\\s*(?:(${TEMPLATE_BLANK_TYPE_PATTERN})\\s*:\\s*)?([A-Za-z0-9_][A-Za-z0-9_ ]*?)\\s*%\\}`,
  "gi",
);

function normaliseName(name: string): string {
  return name.trim().replace(/\s+/g, " ");
}

function blankKey(name: string): string {
  return normaliseName(name).toLowerCase();
}

function blankLabel(name: string): string {
  const spaced = name.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The written type, or Text for an untyped Blank. */
function writtenType(type: string | undefined): TemplateBlankType {
  return (type?.toLowerCase() as TemplateBlankType | undefined) ?? "text";
}

function parseBlank(match: RegExpExecArray): TemplateBlank {
  const name = normaliseName(match[2]);
  return {
    key: blankKey(name),
    name,
    label: blankLabel(name),
    type: writtenType(match[1]),
  };
}

/** Distinct Blanks across the given texts, in order of first appearance. */
export function findTemplateBlanks(...texts: string[]): TemplateBlank[] {
  const byKey = new Map<string, TemplateBlank>();
  for (const text of texts) {
    const re = new RegExp(BLANK_RE.source, BLANK_RE.flags);
    let match: RegExpExecArray | null;
    while ((match = re.exec(text || "")) !== null) {
      const blank = parseBlank(match);
      if (!byKey.has(blank.key)) byKey.set(blank.key, blank);
    }
  }
  return Array.from(byKey.values());
}

/** A Date Blank's value, `yyyy-MM-dd`, read as a local date (not UTC midnight). */
export function dateFromBlankValue(value: string): Date | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!parts) return null;
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

/** The value kept for a picked date; empty when nothing valid is picked. */
export function dateBlankValue(date: Date | null): string {
  if (!date || Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The text a filled Blank becomes, written the way the rest of the app writes it. */
export function formatBlankValue(
  type: TemplateBlankType,
  value: string,
): string {
  const trimmed = value.trim();
  switch (type) {
    case "date":
      return formatEventDisplayDate(dateFromBlankValue(trimmed)) || trimmed;
    case "amount": {
      const digits = trimmed.replace(/[^\d.-]/g, "");
      const amount = Number(digits);
      return digits && Number.isFinite(amount)
        ? formatLedgerCurrency(amount)
        : trimmed;
    }
    case "time":
    case "text":
      return trimmed;
  }
}

/**
 * Replace every filled Blank in `text` with its formatted value. A Blank's type
 * comes from `blanks` (the first one found wins); unfilled Blanks stay as they are.
 * `write` wraps each value as it goes in, e.g. to escape it.
 */
export function fillTemplateBlanks(
  text: string,
  blanks: TemplateBlank[],
  values: TemplateBlankValues,
  write: (formatted: string) => string = (formatted) => formatted,
): string {
  const typeByKey = new Map(blanks.map((b) => [b.key, b.type]));
  return (text || "").replace(
    new RegExp(BLANK_RE.source, BLANK_RE.flags),
    (whole, type: string | undefined, name: string) => {
      const key = blankKey(name);
      const value = values[key];
      if (!value?.trim()) return whole;
      return write(
        formatBlankValue(typeByKey.get(key) ?? writtenType(type), value),
      );
    },
  );
}

/** A Notice's title and text, before or after its Blanks are filled in. */
export type TemplateNoticeText = { title: string; body: string };

/**
 * Fill a Template's title and text. Values written into a rich-text body are
 * escaped so they read as typed.
 */
export function fillTemplateNotice(
  notice: TemplateNoticeText & { blanks: TemplateBlank[] },
  values: TemplateBlankValues,
): TemplateNoticeText {
  return {
    title: fillTemplateBlanks(notice.title, notice.blanks, values),
    body: fillTemplateBlanks(
      notice.body,
      notice.blanks,
      values,
      isPlainNoticeBody(notice.body) ? undefined : escapeNoticeBodyText,
    ),
  };
}

/**
 * Whether `current` differs from the Template filled with `values`, i.e. the
 * title or text was edited by hand since Fill in the values.
 */
export function isHandEdited(
  notice: TemplateNoticeText & { blanks: TemplateBlank[] },
  values: TemplateBlankValues,
  current: TemplateNoticeText,
): boolean {
  const filled = fillTemplateNotice(notice, values);
  return current.title !== filled.title || current.body !== filled.body;
}

/** Text with each Blank shown by its label, e.g. "Closed on [Closing date]". */
export function labelTemplateBlanks(text: string): string {
  return (text || "").replace(
    new RegExp(BLANK_RE.source, BLANK_RE.flags),
    (_whole, _type: string | undefined, name: string) =>
      `[${blankLabel(normaliseName(name))}]`,
  );
}

/** Longest name a new Blank can have. */
export const BLANK_NAME_MAX_LENGTH = 40;

/**
 * Why a new Blank's name can't be used, or undefined when it can. Stricter
 * than what's recognised: older Blanks may use underscores, new ones don't.
 */
export function blankNameError(name: string): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return "Give this value a name.";
  if (trimmed.length > BLANK_NAME_MAX_LENGTH) {
    return `Keep the name to ${BLANK_NAME_MAX_LENGTH} characters or fewer.`;
  }
  if (!/^[A-Za-z0-9 ]+$/.test(trimmed)) {
    return "Use only letters, numbers and spaces.";
  }
  return undefined;
}

/** The Blank text inserted for a type and name, e.g. `{% date: Closing date %}`. */
export function templateBlankToken(
  type: TemplateBlankType,
  name: string,
): string {
  return `{% ${type}: ${normaliseName(name)} %}`;
}
